<script>import Button from "../../../components/Button.svelte";
import { afterUpdate, onMount } from "svelte";
import TlmdDomainCaption from "../../TlmdDomainCaption.svelte";
import ReportConfigurator from "../../report/Configurator.svelte";
import { API } from "../../../api/api.js";
import { theme } from "../../../stores/theme.js";
import { toLocale } from "../../../i18n/translatable.js";
import {
  locale as globalLocale,
  timeZone as globalTimeZone,
  timeRange
} from "../global-store.js";
import {
  extractReceptors,
  extractSources
} from "./modelling-report-helpers.js";
import * as XLSX from "xlsx";
import {
  GoiTypeId
} from "../../d3/legacy/modelling/types.js";
import { humanTimeZone } from "../../util/i18n-time-util.js";
import { getDateTimeFormatters } from "../../d3/time-axis/timeline-scale.js";
import { ModellingGrid } from "../../../grid/grid.js";
import { isq } from "../../../isq/index.js";
export let domain;
export let domainExtName;
export let timeZone = void 0;
export let locale = void 0;
export let rangeStart;
export let rangeEnd;
export let rangeType;
export let fixedRangeType = false;
export let host = void 0;
export let credentials = void 0;
export let modellingTypeId;
export let includeDomain = false;
export let include = (highestImpact) => highestImpact !== void 0 ? highestImpact >= 1e-3 : false;
export let assets = "";
let mounted = false;
let started = false;
let dataRange = void 0;
let headerElem;
let headerHeight = 0;
afterUpdate(() => {
  const height = headerElem ? headerElem.getBoundingClientRect().height : 0;
  if (height !== headerHeight) {
    headerHeight = height;
  }
});
$timeRange = { start: new Date(rangeStart), end: new Date(rangeEnd) };
$globalLocale = locale ?? $globalLocale;
$globalTimeZone = timeZone ?? $globalTimeZone;
$:
  reportConfiguration = {
    range: {
      start: $timeRange.start.getTime(),
      end: $timeRange.end.getTime(),
      type: rangeType
    }
  };
$:
  formatters = getDateTimeFormatters(
    $globalLocale,
    $globalTimeZone,
    "hours"
  );
$:
  startMarker = {
    millis: $timeRange.start.getTime(),
    label: formatters.bound($timeRange.start)
  };
$:
  endMarker = {
    millis: $timeRange.end.getTime(),
    label: formatters.bound($timeRange.end)
  };
$:
  rangeAsText = `${startMarker?.label ?? $timeRange.start} - ${endMarker?.label ?? $timeRange.end}`;
$: {
  if (reportConfiguration.range.end !== reportConfiguration.range.start) {
    getData(reportConfiguration.range);
  }
}
let modellingType;
let modellingRuns;
const sourceTimehUnit = isq.getUnit("time.metric.second");
const targetTimehUnit = isq.getUnit("time.metric.minute");
const stu2ttu = sourceTimehUnit.getConverter(targetTimehUnit);
const sourceLengthUnit = isq.getUnit("length.metric.metre");
const targetLengthUnit = isq.getUnit("length.metric.kilo.metre");
const slu2tlu = sourceLengthUnit.getConverter(targetLengthUnit);
const sourceSurfaceUnit = isq.getUnit("area.metric.metre2");
const targetSurfaceUnit = isq.getUnit("area.metric.kilometre2");
const ssu2tsu = sourceSurfaceUnit.getConverter(targetSurfaceUnit);
let receptors;
let sources;
let grid;
$:
  exportTitle = getExportTitle(modellingType);
onMount(async () => {
  mounted = true;
  console.log("Ready...");
  API.init(host ? host : "");
  API.setDomain(domain);
  if (credentials) {
    API.setCredentials(credentials.usr, credentials.pwd);
  } else {
    API.reloadCredentials();
  }
  await API.signIn();
  modellingType = await API.getModellingType(modellingTypeId);
  grid = new ModellingGrid(modellingType.grid);
  const goisOfType = await API.getGoisOfType(
    GoiTypeId.ImpactMonitor,
    void 0,
    //active or not
    false
    //not deleted
  );
  receptors = extractReceptors(
    goisOfType,
    goisOfType.length === 0 || includeDomain ? grid : void 0,
    false
  );
  started = true;
  await getData(reportConfiguration.range);
});
const openProperties = (key) => {
  const propertiesDiv = document.querySelector(".report div.properties");
  propertiesDiv.classList.remove("closed");
  let configuratorClassName = key;
  propertiesDiv.querySelectorAll("div.configurator").forEach((elem) => {
    if (elem.classList.contains(configuratorClassName)) {
      elem.classList.add("open");
    } else {
      elem.classList.remove("open");
    }
  });
};
const closeProperties = () => {
  const propertiesDiv = document.querySelector(".report div.properties");
  document.querySelector(".report div.properties")?.classList.add("closed");
};
const getExportTitle = (modellingType2) => {
  if (modellingType2) {
    let title = modellingType2.label;
    return title;
  }
  return "...";
};
const getData = async (range) => {
  const doGetData = started && range && range.start !== void 0 && range.end !== void 0 && (dataRange == void 0 || dataRange.start != range.start || dataRange.end != range.end);
  if (doGetData) {
    dataRange = range;
    modellingRuns = await API.getModellingRuns(
      new Date(range.start).toISOString(),
      new Date(range.end).toISOString(),
      [modellingTypeId],
      true,
      true,
      //active
      false
      //not deleted
    );
    sources = extractSources(modellingType, modellingRuns);
  }
};
const isPoint = (goi) => {
  return goi.geometry.size === void 0;
};
const getReceptorPosition = (goi, coordinate) => {
  if (isPoint(goi)) {
    const pt = goi.geometry;
    return getPositionCoordinate(pt.xy[coordinate]);
  } else {
    const ar = goi.geometry;
    return getPositionCoordinate(ar.utmAverage[coordinate]);
  }
};
const getReceptorSize = (goi) => {
  if (isPoint(goi)) {
    const pt = goi.geometry;
    return "0";
  } else {
    const ar = goi.geometry;
    return ssu2tsu.convert(ar.size).toFixed(2);
  }
};
const getPositionCoordinate = (coordinate) => slu2tlu.convert(coordinate).toFixed(3);
const getHighestImpact = (modellingRunId, interval, goiId) => {
  const impact = sources.map[modellingRunId].modellingRun.profile[interval]?.analysis?.distribution[goiId]?.max;
  if (include(impact)) {
    if (impact < 1) {
      return impact.toFixed(3);
    } else if (impact < 10) {
      return impact.toFixed(2);
    } else if (impact < 100) {
      return impact.toFixed(1);
    } else {
      return impact.toFixed(0);
    }
  }
  return void 0;
};
let propertiesTable;
let sourcesTable;
let receptorsTable;
let impactTable;
const exportData = () => {
  let workbook = XLSX.utils.book_new();
  if (propertiesTable) {
    let propertiesSheet = XLSX.utils.table_to_sheet(propertiesTable);
    XLSX.utils.book_append_sheet(
      workbook,
      propertiesSheet,
      "Properties",
      true
    );
  }
  if (sourcesTable) {
    let sourcesSheet = XLSX.utils.table_to_sheet(sourcesTable);
    XLSX.utils.book_append_sheet(workbook, sourcesSheet, "Simulations", true);
  }
  if (receptorsTable) {
    let receptorsSheet = XLSX.utils.table_to_sheet(receptorsTable);
    XLSX.utils.book_append_sheet(workbook, receptorsSheet, "Receptors", true);
  }
  if (impactTable) {
    let impactsSheet = XLSX.utils.table_to_sheet(impactTable);
    XLSX.utils.book_append_sheet(workbook, impactsSheet, "Impacts", true);
  }
  XLSX.writeFile(workbook, `${exportTitle} Export - ${rangeAsText}.xlsx`);
};
</script>

<svelte:head>
  <title>{exportTitle} Export - {rangeAsText}</title>
</svelte:head>
<!--use this test to avoid seeing light theme first while dark has been chosen (the flicker of brightness) -->
{#if mounted && $globalLocale}
  <div class="report {$theme}" style="--headerHeight:{headerHeight}px;">
    <div class="content">
      <header bind:this={headerElem} class="header">
        <div class="caption">
          <div class="noprint float">
            <Button
              story={"button.primary.medium"}
              on:click={(ev) => exportData()}
              ><span class="icon-download" /></Button
            >
            <Button
              story={$theme === "dark"
                ? "button.secondary.medium.icon"
                : "button.primary.medium.icon"}
              on:click={(ev) => openProperties("general")}
              ><span class="glyph-cog" /></Button
            >
          </div>
          <TlmdDomainCaption {assets} domain={domainExtName} /> - {exportTitle} Export
        </div>
        <div class="info">
          {rangeAsText} ({humanTimeZone($globalTimeZone, $globalLocale)})
        </div>
        <div class="info">
          <!-- info here (e.g. number of data points)-->
        </div>
      </header>
      <div class="report-body tlmd-scroll">
        {#if modellingType}
          <table bind:this={propertiesTable}>
            <thead>
              <tr><th>Property</th><th>Value</th></tr>
            </thead>
            <tbody>
              <tr><td>Domain</td><td>{domainExtName}</td></tr>
              <tr><td>UTM zone</td><td>{modellingType.grid.zoneLabel}</td></tr>
              <tr><td>Time Zone</td><td>{$globalTimeZone}</td></tr>
              <tr
                ><td>Start Time</td><td>{$timeRange.start.toISOString()}</td
                ></tr
              >
              <tr><td>End Time</td><td>{$timeRange.end.toISOString()}</td></tr>
              <tr
                ><td>Interval Count</td><td
                  >{($timeRange.end.getTime() - $timeRange.start.getTime()) /
                    (1000 * grid.getTimeStepInSeconds())}</td
                ></tr
              >
              <tr
                ><td>Interval Unit</td><td
                  >{targetTimehUnit.getPrefixedSymbol()}</td
                ></tr
              >
              <tr
                ><td>Interval</td><td
                  >{stu2ttu.convert(grid.getTimeStepInSeconds())}</td
                ></tr
              >
              <tr
                ><td>Interval Duration</td><td
                  >PT{stu2ttu.convert(grid.getTimeStepInSeconds())}M</td
                ></tr
              >
              <tr
                ><td>Impact Step Unit</td><td
                  >{sourceLengthUnit.getPrefixedSymbol()}</td
                ></tr
              >
              <tr
                ><td>Impact Step X (m)</td><td>{modellingType.impact.step.x}</td
                ></tr
              >
              <tr
                ><td>Impact Step Y (m)</td><td>{modellingType.impact.step.y}</td
                ></tr
              >
              <tr
                ><td>Result Unit</td><td
                  >{grid.getUnit().getPrefixedSymbol()}</td
                ></tr
              >
            </tbody>
            <tfoot> </tfoot>
          </table>
        {/if}
        {#if receptors && receptors.ids.length > 0}
          <table bind:this={receptorsTable}>
            <thead>
              <tr
                ><th>Receptor Id</th><th>Receptor Name</th><th>Type</th><th
                  >Zone</th
                ><th
                  >UTM {grid.getUTM().zoneLabel()} x ({targetLengthUnit.getPrefixedSymbol()})</th
                ><th
                  >UTM {grid.getUTM().zoneLabel()} y ({targetLengthUnit.getPrefixedSymbol()})</th
                ><th>Size ({targetSurfaceUnit.getPrefixedSymbol()})</th></tr
              >
            </thead>
            <tbody>
              {#each receptors.ids as receptorId}
                <tr
                  ><td>{receptorId}</td><td>{receptors.map[receptorId].name}</td
                  ><td>{isPoint(receptors.map[receptorId]) ? "pt" : "ar"}</td
                  ><td
                    >{isPoint(receptors.map[receptorId])
                      ? "Location"
                      : receptors.map[receptorId].isHole
                        ? "Exterior"
                        : "Interior"}</td
                  ><td>{getReceptorPosition(receptors.map[receptorId], 0)}</td
                  ><td>{getReceptorPosition(receptors.map[receptorId], 1)}</td
                  ><td>{getReceptorSize(receptors.map[receptorId])}</td></tr
                >
              {/each}
            </tbody>
            <tfoot> </tfoot>
          </table>
        {/if}
        {#if sources && sources.ids.length > 0}
          <table bind:this={sourcesTable}>
            <thead>
              <tr
                ><th>Simulation Id</th><th>Description</th><th>Source(s)</th><th
                  >UTM {grid.getUTM().zoneLabel()} x ({targetLengthUnit.getPrefixedSymbol()})</th
                ><th
                  >UTM {grid.getUTM().zoneLabel()} y ({targetLengthUnit.getPrefixedSymbol()})</th
                ></tr
              >
            </thead>
            <tbody>
              {#each sources.ids as sourceId}
                <tr
                  ><td>{sourceId}</td><td>{sources.map[sourceId].name}</td><td
                    >{sources.map[sourceId].source.label}</td
                  ><td
                    >{getPositionCoordinate(
                      sources.map[sourceId].source.location.x,
                    )}</td
                  ><td
                    >{getPositionCoordinate(
                      sources.map[sourceId].source.location.y,
                    )}</td
                  ></tr
                >
              {/each}
            </tbody>
            <tfoot> </tfoot>
          </table>
        {/if}
        {#if modellingRuns && modellingRuns.length > 0}
          <table bind:this={impactTable}>
            <thead>
              <tr
                ><th>Time</th><th>Interval</th><th>Simulation</th><th
                  >Receptor</th
                ><th>Highest Impact ({grid.getUnit().getPrefixedSymbol()})</th
                ></tr
              >
            </thead>
            <tbody>
              {#each modellingRuns[0].profile as _, intervalIndex}
                {#each sources.ids as sourceId}
                  {#each receptors.ids as receptorId}
                    {#if getHighestImpact(sourceId, intervalIndex, receptorId) !== undefined}
                      <tr
                        ><td
                          >{new Date(
                            sources.map[sourceId].modellingRun.profile[
                              intervalIndex
                            ].start,
                          ).toISOString()}</td
                        ><td>{intervalIndex}</td><td>{sourceId}</td><td
                          >{receptorId}</td
                        ><td
                          >{getHighestImpact(
                            sourceId,
                            intervalIndex,
                            receptorId,
                          )}</td
                        ></tr
                      >
                    {/if}
                  {/each}
                {/each}
              {/each}
            </tbody>
            <tfoot> </tfoot>
          </table>
        {/if}
      </div>
    </div>
    <div class="noprint properties right closed">
      <div class="noprint float close">
        <Button
          story={$theme === "dark"
            ? "button.secondary.medium.icon"
            : "button.primary.medium.icon"}
          on:click={closeProperties}><span class="glyph-x" /></Button
        >
      </div>
      <div class="header">
        <span class="caption">Properties</span>
        <!--ThemeSwitch /-->
      </div>
      <div class="configurator general open">
        <ReportConfigurator
          bind:value={reportConfiguration}
          {fixedRangeType}
          locale={toLocale($globalLocale)}
        />
      </div>
    </div>
  </div>
{/if}

<style>
  div.report {
    --report-caption-font-size: 24px;
    --report-header-info-font-size: 14px;
    --report-section-header-font-size: 18px;
    --report-chart-header-font-size: 16px;
    --report-footer-font-size: 14px;

    --base-color-bg-default: #2d2f36;
    --base-color-primary-default: #6bae85;
    --base-color-onSurface-secondary: #1a1b1f;
    --base-color-onSurface-primary: #dbe4e7;
    --core-color-crayon-honeyYellow: #fbc62f;
  }

  div.report {
    --surface-color: var(--base-color-onSurface-primary);
    --header-surface-color: var(--core-color-crayon-honeyYellow);
    --header-onSurface-color: var(--base-color-onSurface-secondary);
    --section-onSurface-color: var(--base-color-onSurface-secondary);
    --data-header-onSurface-color: var(--base-color-onSurface-primary);
    --scroll-color: var(--base-color-onSurface-secondary);
  }

  div.report.dark {
    --surface-color: var(--base-color-bg-default);
    --header-surface-color: var(--base-color-onSurface-secondary);
    --header-onSurface-color: var(--base-color-primary-default);
    --section-onSurface-color: var(--base-color-onSurface-primary);
    --data-header-onSurface-color: var(--base-color-bg-default);
    --scroll-color: var(--base-color-primary-default);
  }
  @media print {
    div.report.dark,
    div.report {
      --surface-color: transparent;
      --header-surface-color: transparent;
      --header-onSurface-color: var(--base-color-onSurface-secondary);
      --section-onSurface-color: var(--base-color-onSurface-secondary);
      --data-header-onSurface-color: white;
    }
  }

  .float {
    float: inline-end;
    position: relative;
    z-index: 10000;
  }
  div.properties {
    border-color: var(--section-onSurface-color);
  }
  div.properties.right {
    border-left: 1px solid var(--header-surface-color);
  }
  div.properties.right:not(.closed) {
    min-width: 425px;
  }
  div.properties.top {
    border-bottom: 1px solid var(--header-surface-color);
  }
  div.properties.closed {
    width: 0px;
    border: none;
  }
  div.properties.closed :global(*) {
    position: absolute;
    left: -9999px;
  }

  div.properties div.header {
    background-color: var(--header-surface-color);
    border-left: 2px solid var(--surface-color);
    padding: 1em;
    color: var(--section-onSurface-color);
  }
  div.properties div.header .caption {
    font-weight: 700;
    font-size: 18px;
    margin: 1em 0;
    display: inline-block;
  }

  div.properties .configurator {
    color: var(--section-onSurface-color);
  }

  div.properties div.configurator {
    display: none;
  }
  div.properties div.configurator.open {
    display: block;
  }

  div.properties.closed div.close {
    display: none;
  }
  /* div.properties div.configurator div.configurator-controls {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  } */

  div.report {
    background-color: var(--surface-color);
  }

  .report table {
    border: 0;
    border-collapse: collapse;
    width: 100%;
  }

  .report .content > header,
  .report table thead {
    background-color: var(--header-surface-color);
  }
  .report table thead tr th {
    font-weight: normal;
    text-align: unset;
  }

  div.report :global(.tlmd-domain-caption) {
    --domain-font-size: var(--report-caption-font-size);
  }

  .report .content > header,
  .report table thead tr th .header {
    color: var(--header-onSurface-color);
  }
  table thead tr th {
    color: var(--header-onSurface-color);
  }
  .report .content > header .caption,
  .report table thead tr th .header .caption {
    font-size: var(--report-caption-font-size);
    font-weight: bold;
  }
  .report .content > header .info,
  .report table thead tr th .header .info {
    font-size: var(--report-header-info-font-size);
    font-weight: bold;
    margin-top: 0.5em;
  }

  .report header.header {
    margin-bottom: 1rem;
  }

  .report table tbody tr.section td {
    color: var(--section-onSurface-color);
  }
  table tbody tr td {
    color: var(--section-onSurface-color);
  }

  .report table tbody tr.section td .header {
    font-size: var(--report-section-header-font-size);
    font-weight: bold;
  }
  .report table tbody tr.section td .time-plot .header,
  .report table tbody tr.section td .chart .header {
    font-size: var(--report-chart-header-font-size);
    font-weight: bold;
  }

  .report table tfoot tr td {
    color: var(--section-onSurface-color);
  }

  .report .content > header,
  .report table thead th {
    padding: 1em;
  }
  .report table tfoot td {
    padding: 1em;
  }
  .report table tbody td {
    padding: 0 1em 1em 1em;
  }
  .report {
    font-family: Roboto;
  }

  div.statistics.pie {
    display: inline-block;
    min-width: 360px;
    width: calc(50% - 1em);
    max-width: 640px;
    margin: 0.5em;
    border: 1px solid var(--section-onSurface-color);
    border-radius: 0.5em;
    padding: 1em;
  }
  .report table tbody td :global(svg) {
    color: var(--section-onSurface-color);
  }

  /* .report div.distribution {
		text-align: center;
	} */

  .time-plot .header {
    text-align: center;
    margin-top: 2rem;
  }
  @media screen {
    .report {
      display: grid;
    }

    .report {
      grid-template-columns: 1fr auto;
    }

    .report > div.content > div.report-body {
      max-height: calc(100vh - var(--headerHeight) - 0px);
      overflow-y: auto;
    }

    /* Styling the scrollbar in Firefox  */
    .tlmd-scroll {
      scrollbar-color: var(--scroll-color) transparent;
      scrollbar-width: thin;
    }
    /* Styling the scrollbar in Chrome and Safari */
    .tlmd-scroll::-webkit-scrollbar {
      width: 3px;
    }
    .tlmd-scroll::-webkit-scrollbar-thumb {
      background-color: var(
        --scroll-color
      ); /* Set thumb color for Chrome and Safari */
    }
    .tlmd-scroll::-webkit-scrollbar-track {
      background-color: transparent; /* Set track color for Chrome and Safari */
    }

    .report .content {
      grid-column: 1 / 2;
    }

    .report .properties {
      grid-column: 2 / 3;
    }

    .report table :global(svg.plot) {
      max-width: 1292px;
    }
    .not-included {
      -webkit-filter: blur(5px) grayscale(1);
      -moz-filter: blur(5px) grayscale(1);
      -o-filter: blur(5px) grayscale(1);
      -ms-filter: blur(5px) grayscale(1);
      filter: blur(5px) grayscale(1);
    }
  }

  @media print {
    .report {
      display: block;
    }
    .report .content > header {
      display: none;
    }
    .not-included,
    .noprint {
      display: none;
      position: absolute;
      border: none;
      left: -9999px;
    }
    div.statistics.pie {
      min-width: 0;
    }
    div.properties.right.closed,
    div.properties.right {
      min-width: 0;
      width: 0;
    }
  }
</style>
